import React, { useId, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { candidacyService } from '@/shared/services/candidacyService';
import { PREVIEW_LOCK_NOTE_ID } from '@/shared/hooks/usePreviewLock';
import { SAVE_FALLBACK, WriteStatus, refusalOf } from './reviewerStages';

const WHERE_MAX = 200;
const DETAILS_MAX = 2000;

const pad = (n) => String(n).padStart(2, '0');

// A datetime-local input holds a wall-clock time in the browser's own zone.
function toInputValue(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Sets a candidacy's interview time, place and details. The server replaces
 * the three together: a field left empty is cleared, and saving emails the
 * student.
 */
export default function InterviewDetailsForm({
    candidacy,
    disabled,
    onChange,
    onReload,
}) {
    const id = useId();
    const { interview } = candidacy;
    const [when, setWhen] = useState(toInputValue(interview?.when));
    const [where, setWhere] = useState(interview?.where || '');
    const [details, setDetails] = useState(interview?.details || '');
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState(null);
    const [notice, setNotice] = useState('');
    const reason =
        !when && !where.trim() && !details.trim()
            ? 'Give a time, a place or details for the interview.'
            : where.length > WHERE_MAX
              ? `Where can be at most ${WHERE_MAX} characters.`
              : details.length > DETAILS_MAX
                ? `Details can be at most ${DETAILS_MAX.toLocaleString('en-US')} characters.`
                : '';

    const submit = async (event) => {
        event.preventDefault();
        if (disabled || busy || reason) return;
        setBusy(true);
        setRefusal(null);
        setNotice('');
        try {
            // new Date() reads the input in the browser's zone, so the server
            // gets the instant the reviewer meant.
            const updated = await candidacyService.setInterview(candidacy.id, {
                when: when ? new Date(when).toISOString() : undefined,
                where: where.trim() ? where : undefined,
                details: details.trim() ? details : undefined,
            });
            setNotice('Interview details saved. The student has been emailed.');
            onChange?.(updated, 'Interview details saved.');
        } catch (err) {
            setRefusal(refusalOf(err, SAVE_FALLBACK));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Form onSubmit={submit} noValidate>
            <Form.Group controlId={`${id}-when`} className="mb-2">
                <Form.Label>When</Form.Label>
                <Form.Control
                    type="datetime-local"
                    value={when}
                    onChange={(e) => setWhen(e.target.value)}
                />
            </Form.Group>
            <Form.Group controlId={`${id}-where`} className="mb-2">
                <Form.Label>Where</Form.Label>
                <Form.Control
                    type="text"
                    maxLength={WHERE_MAX}
                    value={where}
                    onChange={(e) => setWhere(e.target.value)}
                />
            </Form.Group>
            <Form.Group controlId={`${id}-details`} className="mb-2">
                <Form.Label>Details</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    maxLength={DETAILS_MAX}
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    aria-describedby={`${id}-note`}
                />
                <Form.Text id={`${id}-note`} as="div" muted>
                    Saving emails the student these details.
                </Form.Text>
            </Form.Group>
            <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={disabled || busy || Boolean(reason)}
                aria-describedby={
                    [
                        reason ? `${id}-reason` : null,
                        disabled ? PREVIEW_LOCK_NOTE_ID : null,
                    ]
                        .filter(Boolean)
                        .join(' ') || undefined
                }
            >
                Save interview details
            </Button>
            <span
                id={`${id}-reason`}
                className="ms-2 small text-body-secondary"
            >
                {reason}
            </span>
            <WriteStatus
                refusal={refusal}
                notice={notice}
                onReload={onReload}
            />
        </Form>
    );
}
